import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { 
  ArrowLeft,
  CalendarIcon,
  Clock,
  MapPin,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';


const TIME_SLOTS = [
  '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
  '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
];

export const Booking = () => {
  const navigate = useNavigate();
  const { consultationId, centerId } = useParams();
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchCenter();
  }, [centerId]);

  const fetchCenter = async () => {
    try {
      const response = await api.get(`/api/panchkarma-centers/${centerId}`);
      setCenter(response.data);
    } catch (error) {
      console.error('Error fetching center:', error);
      toast.error('Failed to load center details');
      navigate('/panchkarma');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    if (!selectedTime) {
      toast.error('Please select a time slot');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(
        '/api/bookings',
        {
          consultation_id: consultationId === 'new' ? '' : consultationId,
          center_id: centerId,
          preferred_date: format(selectedDate, 'yyyy-MM-dd'),
          preferred_time: selectedTime,
          notes
        }
      );
      setSuccess(true);
      toast.success('Booking request submitted!');
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,23%,96%)]">
        <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,23%,96%)]">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Booking Request Submitted!
          </h2>
          <p className="text-stone-600 mb-8">
            Your booking request for <span className="font-semibold">{center?.name}</span> has been submitted. 
            The center will contact you shortly to confirm your appointment.
          </p>
          <div className="bg-stone-50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-stone-500 mb-1">Preferred Date & Time</p>
            <p className="font-medium text-[hsl(120,10%,18%)]">
              {selectedDate && format(selectedDate, 'MMMM d, yyyy')} at {selectedTime}
            </p>
          </div>
          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="btn-secondary"
            >
              Go to Dashboard
            </Button>
            <Button
              onClick={() => navigate('/panchkarma')}
              className="btn-primary"
            >
              Browse More Centers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="booking-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-stone-200/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-stone-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Book Appointment
            </h1>
            <p className="text-sm text-stone-500">Schedule your Panchkarma session</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Center Info */}
          <div className="card-elevated overflow-hidden">
            <img 
              src={center?.image_url || 'https://images.unsplash.com/photo-1711714956204-e1d84e4d8879'}
              alt={center?.name}
              className="w-full h-48 object-cover"
            />
            <div className="p-6">
              <h2 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {center?.name}
              </h2>
              <div className="flex items-center gap-2 text-stone-500 text-sm mb-4">
                <MapPin className="w-4 h-4" />
                <span>{center?.location}</span>
              </div>
              <p className="text-stone-600 text-sm mb-4">{center?.description}</p>
              <div className="flex flex-wrap gap-2">
                {center?.treatments.map((treatment, index) => (
                  <span key={index} className="px-2 py-1 bg-[hsl(135,15%,33%)/0.1] text-[hsl(135,15%,33%)] rounded-full text-xs">
                    {treatment}
                  </span>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-stone-100">
                <span className="text-[hsl(22,70%,44%)] font-semibold text-lg">{center?.price_range}</span>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div className="consultation-card">
            <h3 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Select Date & Time
            </h3>

            <div className="space-y-6">
              {/* Date Picker */}
              <div>
                <Label className="mb-2 block">Preferred Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Select a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Slot */}
              <div>
                <Label className="mb-2 block">Preferred Time</Label>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger data-testid="time-select">
                    <SelectValue placeholder="Select a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(slot => (
                      <SelectItem key={slot} value={slot}>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {slot}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div>
                <Label className="mb-2 block">Additional Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requests or information..."
                  className="min-h-[100px]"
                  data-testid="notes-input"
                />
              </div>

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full btn-primary text-lg py-6"
                data-testid="submit-booking-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Request Booking'
                )}
              </Button>

              <p className="text-center text-sm text-stone-500">
                The center will contact you to confirm your appointment
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
